import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../components/AppShell";
import { useNavigate } from "react-router-dom";
import useModules from "../hooks/useModules";
import { useScopedModuleId } from "../contexts/ModuleScopeContext";
import { api, formatApiError } from "../lib/api";
import { formatDate, statusBadgeClass } from "../lib/domain";
import { toast } from "sonner";
import { AlertTriangle, Banknote, Trash2, X } from "lucide-react";
import { TableSkeleton } from "../components/SkeletonHelpers";
import EmptyState from "../components/EmptyState";
import RowActions from "../components/RowActions";
import ShowDeletedToggle, { DeletedRowDecorator, DeletedPill } from "../components/ShowDeletedToggle";
import ModuleContextChip from "../components/ModuleContextChip";
import { rowOpenProps } from "../hooks/useRowOpen";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";

const fmtUSD = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Iter60.p8 — Admin/HR can bulk-delete funding rows. Dept mgrs do not
// see the checkboxes or the destructive "Delete ALL" button; RBAC is
// also enforced on the backend endpoints.
const ADMIN_HR_ROLES = new Set(["kaafi_system_admin", "kaafi_hr_manager", "asl_hr_officer"]);

export default function PayrollFunding() {
    const navigate = useNavigate();
    const { byId } = useModules();
    const { user } = useAuth();
    // Iter60.p13 — When inside a module workspace (`/m/{code}/payroll/funding`)
    // we pass `module_id=scopedId` on every request so Admin/HR also see
    // only the current module's rows. Dept Mgrs were already auto-scoped
    // via `module_scope_filter` on the backend.
    const scopedId = useScopedModuleId();
    const canBulkDelete = user && ADMIN_HR_ROLES.has(user.role);

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showDeleted, setShowDeleted] = useState(false);
    // Iter60.p12 — bank-details summary (count of active employees
    // without a bank_name+bank_account_number pair in the current
    // scope). Refreshed alongside the main list.
    const [bankSummary, setBankSummary] = useState(null);

    // Iter60.p8 — selection state. Only tracks non-deleted rows (a
    // soft-deleted row would need Restore, not Delete Again).
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [bulkBusy, setBulkBusy] = useState(false);

    // Iter60.p8 — destructive "Delete ALL across ALL modules" modal.
    const [confirmAllOpen, setConfirmAllOpen] = useState(false);
    const [confirmAllTyped, setConfirmAllTyped] = useState("");
    const [totalCount, setTotalCount] = useState(null);

    const load = () => {
        setLoading(true);
        const params = {};
        if (showDeleted) params.include_deleted = true;
        // Iter60.p13 — scope to current module when inside a workspace.
        if (scopedId) params.module_id = scopedId;
        api.get("/payroll/funding", { params })
            .then(({ data }) => {
                setItems(data);
                // Prune selection to rows still present + still non-deleted.
                setSelectedIds((prev) => {
                    const next = new Set();
                    const validIds = new Set(data.filter((r) => !r.deleted_at).map((r) => r.id));
                    for (const id of prev) if (validIds.has(id)) next.add(id);
                    return next;
                });
            })
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, [showDeleted, scopedId]);

    // Iter60.p12 — pull bank-details summary once per scope change.
    useEffect(() => {
        const params = {};
        if (scopedId) params.module_id = scopedId;
        api.get("/employees/bank-summary", { params })
            .then(({ data }) => setBankSummary(data))
            .catch(() => setBankSummary(null));
    }, [scopedId]);

    const activeRows = useMemo(() => items.filter((r) => !r.deleted_at), [items]);
    const allActiveIds = useMemo(() => activeRows.map((r) => r.id), [activeRows]);
    const allSelected = allActiveIds.length > 0 && allActiveIds.every((id) => selectedIds.has(id));
    const someSelected = selectedIds.size > 0;

    const toggleRow = (id, checked) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (checked) next.add(id); else next.delete(id);
            return next;
        });
    };
    const toggleAll = (checked) => {
        setSelectedIds(checked ? new Set(allActiveIds) : new Set());
    };
    const clearSelection = () => setSelectedIds(new Set());

    // Bulk-delete the selected IDs.
    const deleteSelected = async () => {
        if (selectedIds.size === 0) return;
        setBulkBusy(true);
        try {
            const ids = Array.from(selectedIds);
            const { data } = await api.post("/payroll/funding/bulk-delete", { ids });
            toast.success(`Deleted ${data.deleted_count} funding ${data.deleted_count === 1 ? "row" : "rows"}. Restore via "Show deleted".`);
            clearSelection();
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBulkBusy(false); }
    };

    // Fetch the count that would be deleted if the user confirms.
    const openConfirmAll = async () => {
        setConfirmAllTyped("");
        setTotalCount(null);
        setConfirmAllOpen(true);
        try {
            const { data } = await api.get("/payroll/funding-count");
            setTotalCount(data.count);
        } catch (e) {
            toast.error(formatApiError(e));
            setConfirmAllOpen(false);
        }
    };

    const deleteAll = async () => {
        if (confirmAllTyped !== "DELETE") return;
        setBulkBusy(true);
        try {
            const { data } = await api.post("/payroll/funding/bulk-delete", {
                scope: "all",
                confirm: "DELETE",
                // module_ids omitted → every module.
            });
            toast.success(`Bulk deleted ${data.deleted_count} funding rows across all modules. Reversible via "Show deleted".`);
            setConfirmAllOpen(false);
            setConfirmAllTyped("");
            clearSelection();
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBulkBusy(false); }
    };

    return (
        <AppShell title="Payroll Funding">
            <section className="space-y-5" data-testid="funding-page">
                {/* Iter60.p13 — module context indicator when inside a workspace. */}
                {scopedId && (
                    <div className="flex justify-between items-center">
                        <ModuleContextChip moduleId={scopedId} size="sm" testid="funding-module-chip" />
                        <span className="text-[11px] text-[color:var(--asl-muted)]">
                            Funding rows shown are scoped to this module only.
                        </span>
                    </div>
                )}
                {/* Iter60.p12 — "N employees missing bank details" warning banner.
                    Rendered only when there IS at least one missing employee in
                    the current scope; kept low-key so it doesn't dominate the page. */}
                {bankSummary && bankSummary.missing_bank > 0 && (
                    <div
                        className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-3"
                        data-testid="funding-missing-bank-banner"
                        data-missing-count={bankSummary.missing_bank}
                    >
                        <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
                        <div className="flex-1">
                            <div className="font-semibold">
                                {bankSummary.missing_bank} active {bankSummary.missing_bank === 1 ? "employee is" : "employees are"} missing bank details
                                {scopedId ? " in this module" : " across all modules"}
                                .
                            </div>
                            <div className="text-[11px] mt-0.5">
                                Payroll wire transfers can&apos;t proceed for these employees until Bank Name and Account Number are set on their profile.
                            </div>
                        </div>
                    </div>
                )}
                {/* Iter60.p8 — top action bar: Show deleted toggle + Delete ALL destructive action. */}
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="text-xs text-[color:var(--asl-muted)]">
                        {activeRows.length} active {activeRows.length === 1 ? "row" : "rows"}
                        {items.length !== activeRows.length && (
                            <> · {items.length - activeRows.length} deleted (hidden)</>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        <ShowDeletedToggle value={showDeleted} onChange={setShowDeleted} testid="funding-show-deleted" />
                        {/* Iter60.p13 — hide the cross-module destructive action
                            when inside a module workspace. It would be a UX trap
                            (users would expect it to scope to the current module). */}
                        {canBulkDelete && !scopedId && (
                            <Button
                                variant="outline"
                                className="text-red-700 border-red-300 hover:bg-red-50"
                                onClick={openConfirmAll}
                                disabled={bulkBusy}
                                data-testid="funding-delete-all-btn"
                                title="Soft-delete every funding row across every module. Reversible."
                            >
                                <AlertTriangle className="h-4 w-4 mr-1.5" />
                                Delete ALL across ALL modules
                            </Button>
                        )}
                    </div>
                </div>

                {/* Iter60.p8 — selection toolbar. Appears only when ≥1 row selected. */}
                {canBulkDelete && someSelected && (
                    <div
                        className="flex items-center justify-between px-4 py-3 rounded-md border"
                        style={{ background: "var(--asl-navy-50)", borderColor: "var(--asl-navy-200, #b0b8cc)" }}
                        data-testid="funding-selection-toolbar"
                    >
                        <div className="text-sm font-semibold asl-navy-text" data-testid="funding-selection-count">
                            {selectedIds.size} {selectedIds.size === 1 ? "row" : "rows"} selected
                        </div>
                        <div className="flex items-center gap-2">
                            <Button variant="ghost" size="sm" onClick={clearSelection}
                                    data-testid="funding-clear-selection">
                                <X className="h-3.5 w-3.5 mr-1" /> Clear selection
                            </Button>
                            <Button
                                size="sm"
                                className="bg-red-600 hover:bg-red-700 text-white"
                                onClick={deleteSelected}
                                disabled={bulkBusy}
                                data-testid="funding-delete-selected"
                            >
                                <Trash2 className="h-3.5 w-3.5 mr-1" />
                                Delete selected ({selectedIds.size})
                            </Button>
                        </div>
                    </div>
                )}

                <div className="asl-card overflow-hidden">
                    {loading ? <TableSkeleton rows={5} cols={9} testid="funding-loading" />
                    : items.length === 0 ? (
                        <EmptyState
                            icon={Banknote}
                            title="No funding requests yet"
                            description="Funding requests appear here once a payroll period is approved and submitted to the bank."
                            testid="funding-empty"
                        />
                    )
                    : <table className="asl-table asl-table--zebra" data-testid="funding-table">
                        <thead className="text-[11px] uppercase tracking-wider text-[color:var(--asl-muted)]"
                            style={{ background: "var(--asl-navy-50)" }}>
                            <tr>
                                {canBulkDelete && (
                                    <th className="px-3 py-3 w-[36px]">
                                        <Checkbox
                                            checked={allSelected}
                                            onCheckedChange={(v) => toggleAll(!!v)}
                                            data-testid="funding-select-all"
                                            aria-label="Select all rows on page"
                                        />
                                    </th>
                                )}
                                <th className="text-left px-3 py-3">Request date</th>
                                <th className="text-left px-3 py-3">Module</th>
                                <th className="text-right px-3 py-3">Wages</th>
                                <th className="text-right px-3 py-3">Mgmt fee</th>
                                <th className="text-right px-3 py-3">Total</th>
                                <th className="text-left px-3 py-3">Bank ref</th>
                                <th className="text-left px-3 py-3">Status</th>
                                <th className="text-right px-3 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((f) => {
                                const modCode = byId[f.module_id]?.code;
                                const href = modCode
                                    ? `/m/${String(modCode).toLowerCase()}/payroll/${f.period_id}`
                                    : `/payroll/periods/${f.period_id}`;
                                const isDeleted = !!f.deleted_at;
                                const isChecked = selectedIds.has(f.id);
                                return (
                                <tr key={f.id}
                                    {...rowOpenProps(navigate, href, { testid: `funding-row-${f.id}`, className: "border-t" })}
                                    style={{ borderColor: "var(--asl-border)" }}>
                                    {canBulkDelete && (
                                        <td className="px-3 py-3 align-middle" onClick={(e) => e.stopPropagation()}>
                                            <Checkbox
                                                checked={isChecked}
                                                disabled={isDeleted}
                                                onCheckedChange={(v) => toggleRow(f.id, !!v)}
                                                data-testid={`funding-select-${f.id}`}
                                                aria-label={`Select funding row ${f.bank_reference || f.id}`}
                                            />
                                        </td>
                                    )}
                                    <td className="px-3 py-3 mono text-xs cursor-pointer" onClick={() => navigate(href)}>
                                        <DeletedRowDecorator deleted={isDeleted}>{formatDate(f.request_date?.slice(0, 10))}</DeletedRowDecorator>
                                        {isDeleted && <DeletedPill deletedAt={f.deleted_at} />}
                                    </td>
                                    <td className="px-3 py-3 text-xs">{byId[f.module_id]?.code}</td>
                                    <td className="px-3 py-3 mono text-xs text-right">{fmtUSD(f.requested_amount_worker_wages)}</td>
                                    <td className="px-3 py-3 mono text-xs text-right">{fmtUSD(f.requested_amount_management_fee)}</td>
                                    <td className="px-3 py-3 mono text-xs text-right font-semibold asl-navy-text">{fmtUSD(f.requested_amount_total)}</td>
                                    <td className="px-3 py-3 mono text-xs">{f.bank_reference || "—"}</td>
                                    <td className="px-3 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${statusBadgeClass(f.status)}`}>{f.status}</span></td>
                                    <td className="px-3 py-3 text-right">
                                        <RowActions
                                            entity="payroll_funding"
                                            endpoint="/payroll/funding"
                                            id={f.id}
                                            label={f.bank_reference || f.id.slice(0, 8)}
                                            deleted={isDeleted}
                                            moduleId={f.module_id}
                                            hardDeleteSupported={false}
                                            onChange={load}
                                            testid={`funding-actions-${f.id}`}
                                        />
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>}
                </div>
            </section>

            {/* Iter60.p8 — destructive confirm-all modal. Only wired when canBulkDelete. */}
            {canBulkDelete && (
                <Dialog open={confirmAllOpen} onOpenChange={(o) => { if (!o) { setConfirmAllOpen(false); setConfirmAllTyped(""); } }}>
                    <DialogContent className="max-w-md" data-testid="funding-delete-all-dialog">
                        <DialogHeader>
                            <DialogTitle className="text-red-700 flex items-center gap-2">
                                <AlertTriangle className="h-5 w-5" />
                                Delete ALL payroll funding rows?
                            </DialogTitle>
                            <DialogDescription className="text-xs">
                                This will <b>soft-delete every funding request across every module</b>.
                                Rows remain in the database and can be restored one-by-one via the
                                &ldquo;Show deleted&rdquo; toggle. Recomputing or re-submitting a payroll
                                period will not automatically recreate them.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-3">
                            <div className="rounded-md border border-red-200 bg-red-50 text-red-800 text-sm p-3"
                                 data-testid="funding-delete-all-count">
                                {totalCount === null
                                    ? "Counting rows…"
                                    : totalCount === 0
                                        ? "No non-deleted funding rows exist. Nothing to delete."
                                        : <>Rows about to be deleted: <b>{totalCount}</b></>}
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-semibold" htmlFor="funding-delete-all-typed">
                                    Type <span className="mono">DELETE</span> to confirm
                                </label>
                                <Input
                                    id="funding-delete-all-typed"
                                    autoComplete="off"
                                    value={confirmAllTyped}
                                    onChange={(e) => setConfirmAllTyped(e.target.value)}
                                    placeholder="DELETE"
                                    data-testid="funding-delete-all-input"
                                    disabled={totalCount === 0 || bulkBusy}
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button
                                variant="outline"
                                onClick={() => { setConfirmAllOpen(false); setConfirmAllTyped(""); }}
                                data-testid="funding-delete-all-cancel"
                            >
                                Cancel
                            </Button>
                            <Button
                                className="bg-red-600 hover:bg-red-700 text-white"
                                onClick={deleteAll}
                                disabled={confirmAllTyped !== "DELETE" || bulkBusy || totalCount === 0}
                                data-testid="funding-delete-all-confirm"
                            >
                                {bulkBusy ? "Deleting…" : `Delete all${totalCount ? ` (${totalCount})` : ""}`}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </AppShell>
    );
}
